import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { Badge } from "@capawesome/capacitor-badge";
import { DateTime } from "luxon";
import { trackErrorEvent, trackEvent } from "../utils/analytics";
import { AppPlatform, NotificationType } from "../utils/models";
import axiosInstance from "../utils/networkWrapper";

export interface PushNotificationData {
	notification_id?: string;
	title: string;
	message: string;
	notificationType: string;
	departmentId: string;
	url?: string;
	[key: string]: unknown;
}

const parseNotificationId = (raw: unknown): number | null => {
	if (typeof raw !== "string" || raw === "") return null;
	const parsed = Number(raw);
	return Number.isInteger(parsed) ? parsed : null;
};

class PushNotificationService {
	private isInitialized = false;
	private currentToken: string | null = null;
	private platform = "";
	private currentUserId: string | null = null;

	async initialize(userId: string): Promise<void> {
		if (!Capacitor.isNativePlatform()) {
			return;
		}

		// Don't initialize if same user
		if (this.isInitialized && this.currentUserId === userId) {
			return;
		}

		// Clean up if different user
		if (this.currentUserId && this.currentUserId !== userId) {
			await this.cleanup();
		}

		this.currentUserId = userId;
		this.platform = Capacitor.getPlatform();
		console.log(`Initializing push notifications for ${this.platform}`);

		try {
			// Request permission
			const permissionState = await PushNotifications.requestPermissions();

			if (permissionState.receive !== "granted") {
				console.log(`${this.platform} push notification permission denied`);
				trackEvent("push_notifications_permission_denied", {
					platform: this.platform,
				});
				return;
			}
			// Set up listeners
			this.setupListeners();

			// Register for push notifications
			await PushNotifications.register();

			this.isInitialized = true;
			console.log(`${this.platform} push notifications initialized successfully`);
		} catch (error) {
			console.error(`Failed to initialize ${this.platform} push notifications:`, error);
			trackErrorEvent("push_notifications_init_failed", error, {
				platform: this.platform,
			});
		}
	}

	private setupListeners(): void {
		// Registration success
		console.log("addListener registration");
		PushNotifications.addListener("registration", async (token) => {
			console.log(`${this.platform} push registration success, token:`, token.value);
			this.currentToken = token.value;

			// Store token for backend
			this.storeToken(token.value);

			// Clear badge when registration is success
			await this.removeAllDeliveredNotifications();

			trackEvent("push_notification_registered", {
				platform: this.platform,
				token: token.value,
			});
		});

		console.log("addListener registrationError");
		// Registration error
		PushNotifications.addListener("registrationError", (error) => {
			console.error(`${this.platform} push registration failed:`, error);
			trackErrorEvent("push_notification_registration_failed", error, {
				platform: this.platform,
			});
		});

		// Push notification received (when app is in foreground)
		console.log("addListener pushNotificationReceived");
		PushNotifications.addListener("pushNotificationReceived", (notification) => {
			console.log(`${this.platform} notification received:`, notification);

			// Platform-specific handling
			this.handleNotificationReceived(notification);

			trackEvent("push_notification_received", {
				platform: this.platform,
				notification_id: notification.id,
				notification_type: notification.data?.notificationType,
			});
		});

		console.log("addListener pushNotificationActionPerformed");
		// Push notification clicked when app is in background/closed
		PushNotifications.addListener("pushNotificationActionPerformed", async (notification) => {
			console.log(`${this.platform} notification clicked:`, notification);

			const data = notification.notification.data as PushNotificationData;
			const notificationId = parseNotificationId(data?.notification_id);

			await this.clearBadge();

			if (notificationId !== null) {
				this.markNotificationClicked(notificationId);
			}

			this.handleNotificationClick(data);

			trackEvent("push_notification_clicked", {
				platform: this.platform,
				notification_id: notificationId ?? notification.notification.id,
				notification_type: data?.notificationType,
				action: notification.actionId,
			});
		});
	}

	private async storeToken(token: string): Promise<void> {
		try {
			// Store token with platform prefix for better organization
			localStorage.setItem(`${this.platform}_push_token`, token);

			// Send token to backend
			await this.sendTokenToBackend(token);
		} catch (error) {
			console.error(`Failed to store ${this.platform} push token:`, error);
		}
	}

	private markNotificationClicked(notificationId: number): void {
		trackEvent("push_notification_mark_clicked_start", { notification_id: notificationId });
		axiosInstance
			.post("/staffing/mark_notification_clicked", { notification_id: notificationId })
			.then(() => {
				trackEvent("push_notification_mark_clicked_success", { notification_id: notificationId });
			})
			.catch((error) => {
				trackErrorEvent("push_notification_mark_clicked_error", error, { notification_id: notificationId });
			});
	}

	private handleNotificationReceived(notification: unknown): void {
		// Platform-specific handling
		if (this.platform === AppPlatform.IOS) {
			// iOS handles badges automatically
			console.log("iOS notification received - badge handled automatically");
		} else if (this.platform === AppPlatform.Android) {
			// Android might need custom badge handling
			console.log("Android notification received - custom badge handling if needed");
		}

		// Common handling for both platforms
		console.log("Notification received in foreground:", notification);
	}

	private handleNotificationClick(data: PushNotificationData): void {
		if (data?.url) {
			window.location.href = data.url;
			return;
		}

		// BE sends snake_case keys; the typed camelCase `notificationType` is unused for routing.
		const notificationType = typeof data?.notification_type === "string" ? data.notification_type : undefined;
		const shiftDateRaw = typeof data?.shift_date === "string" ? data.shift_date : undefined;
		const shiftType = typeof data?.shift_type === "string" ? data.shift_type : undefined;

		if (notificationType === NotificationType.OpenShift) {
			// `shift_date` is a calendar date; BE may send it as `YYYY-MM-DD` or as an ISO
			// datetime. Parse in UTC so the calendar-day extraction is deterministic
			// regardless of the device's local timezone.
			const shiftDate = shiftDateRaw ? DateTime.fromISO(shiftDateRaw, { zone: "utc" }).toISODate() : null;
			if (shiftDate && shiftType) {
				window.location.href = `/open-shifts?highlight=${shiftDate}&shift_type=${shiftType}`;
				return;
			}
			window.location.href = "/open-shifts";
			return;
		}

		window.location.href = "/notifications";
	}

	async removeAllDeliveredNotifications(): Promise<void> {
		try {
			// Works for both iOS and Android
			await PushNotifications.removeAllDeliveredNotifications();
			console.log(`${this.platform} all delivered notifications removed`);
		} catch (error) {
			console.error(`Failed to remove ${this.platform} all delivered notifications:`, error);
		}
	}

	async clearBadge(): Promise<void> {
		try {
			// Clear badge count locally
			await Badge.set({ count: 0 });
			console.log(`${this.platform} badge cleared successfully`);

			// Sync badge count with backend
			await axiosInstance.post("/users/clear-badge-count");
			console.log("Badge count synced with backend");

			// Additional Android-specific badge clearing
			if (this.platform === AppPlatform.Android) {
				console.log("Android: Additional badge clearing steps");
				// Some Android devices need additional steps
				try {
					await PushNotifications.removeAllDeliveredNotifications();
					console.log("Android: All delivered notifications removed");
				} catch (error) {
					console.log("Android: Error removing delivered notifications:", error);
				}
			}
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : "Unknown error";
			console.error(`Failed to clear badge: ${errorMessage}`);
			trackErrorEvent("badge_clear_failed", error, {
				platform: this.platform,
				error_details: errorMessage,
			});
		}
	}

	async sendTokenToBackend(token: string): Promise<void> {
		console.log("push token to backend", token);
		try {
			await axiosInstance.post("/users/push-token", {
				token,
				platform: this.platform,
			});

			console.log(`${this.platform} push token sent to backend successfully`);
		} catch (error) {
			console.error(`Failed to send ${this.platform} push token to backend:`, error);
			// Don't throw error - this will work once backend is ready
			trackErrorEvent("push_token_backend_send_failed", error, {
				platform: this.platform,
			});
		}
	}

	async getStoredToken(): Promise<string | null> {
		return localStorage.getItem(`${this.platform}_push_token`) || this.currentToken;
	}

	async removeAllListeners(): Promise<void> {
		await PushNotifications.removeAllListeners();
	}

	async cleanup(): Promise<void> {
		try {
			console.log(`Cleaning up ${this.platform} push notifications`);

			// Remove all listeners
			await this.removeAllListeners();

			// Clear stored tokens
			localStorage.removeItem(`${this.platform}_push_token`);

			// Reset state
			this.currentToken = null;
			this.isInitialized = false;
			this.currentUserId = null;

			console.log(`${this.platform} push notifications cleaned up successfully`);
		} catch (error) {
			console.error(`Failed to cleanup ${this.platform} push notifications:`, error);
		}
	}

	// future use - push notification in android vs ios
	// Platform utility methods
	getPlatform(): string {
		return this.platform;
	}

	isIOS(): boolean {
		return this.platform === AppPlatform.IOS;
	}

	isAndroid(): boolean {
		return this.platform === AppPlatform.Android;
	}

	async checkPermissionStatus(): Promise<string> {
		if (!Capacitor.isNativePlatform()) {
			return "granted";
		}
		const permission = await PushNotifications.checkPermissions();
		return permission.receive;
	}
}

export const pushNotificationService = new PushNotificationService();
